import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, Phone } from 'lucide-react';
import { BookingDateOption, DaySlot, reservesApi } from '../services/reservesApi';

const BOOKING_PHONE_DISPLAY = '+7 495 576-20-90';
const BOOKING_PHONE_TEL = '+74955762090';
const DEFAULT_DURATION = 90;

function todayIsoMoscow(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '01';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
    return `+7${digits.slice(1)}`;
  }
  if (digits.length === 10) return `+7${digits}`;
  if (raw.trim().startsWith('+')) return `+${digits}`;
  return raw.trim();
}

function formatDateLabel(iso: string, options: BookingDateOption[]): string {
  return options.find((d) => d.date === iso)?.label ?? iso;
}

export default function LandingBookingScreen() {
  const [date, setDate] = useState(todayIsoMoscow);
  const [time, setTime] = useState('');
  const [guestsCount, setGuestsCount] = useState(2);
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');

  const [dates, setDates] = useState<BookingDateOption[]>([]);
  const [availableSlots, setAvailableSlots] = useState<DaySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    requestId: string;
    date: string;
    time: string;
    guestsCount: number;
  } | null>(null);

  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    return () => document.documentElement.classList.remove('landing-page');
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingSlots(true);
    setError(null);
    reservesApi
      .getDaySlots({ date, guestsCount, durationInMinutes: DEFAULT_DURATION })
      .then((data) => {
        if (cancelled) return;
        setDates(data.dates ?? []);
        const slots = data.availableSlots ?? [];
        setAvailableSlots(slots);
        setTime((prev) => {
          if (prev && slots.some((s) => s.time === prev)) return prev;
          return slots[0]?.time ?? '';
        });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setAvailableSlots([]);
        setTime('');
        setError(e.message || 'Не удалось загрузить слоты');
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, guestsCount]);

  const dateOptions = dates.length
    ? dates
    : Array.from({ length: 7 }, (_, i) => {
        const base = new Date(`${todayIsoMoscow()}T12:00:00`);
        base.setDate(base.getDate() + i);
        const pad = (n: number) => String(n).padStart(2, '0');
        const iso = `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`;
        return { date: iso, label: iso, weekday: '' };
      });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const name = customerName.trim();
    const normalizedPhone = normalizePhone(phone);
    if (!name) {
      setError('Укажите имя');
      return;
    }
    if (!/^\+7\d{10}$/.test(normalizedPhone)) {
      setError('Укажите телефон в формате +7XXXXXXXXXX');
      return;
    }
    if (!time) {
      setError('Выберите время');
      return;
    }

    setSubmitting(true);
    try {
      const note = comment.trim();
      const result = await reservesApi.create({
        phone: normalizedPhone,
        customerName: name,
        guestsCount,
        estimatedStartTime: reservesApi.toIikoDateTime(date, time),
        durationInMinutes: DEFAULT_DURATION,
        comment: note
          ? `Онлайн-заявка. ${note}`
          : 'Онлайн-заявка. Посадить за свободный стол под компанию.',
      });
      setSuccess({
        requestId: result.requestId,
        date,
        time,
        guestsCount,
      });
    } catch (err: any) {
      setError(err?.message || 'Не удалось отправить заявку');
    } finally {
      setSubmitting(false);
    }
  }

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 16,
    padding: '14px 16px',
    color: '#fff',
    fontFamily: 'Manrope, sans-serif',
    outline: 'none',
    colorScheme: 'dark',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 11,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.4)',
    fontFamily: 'Manrope, sans-serif',
    marginBottom: 8,
  };

  return (
    <div className="landing-root min-h-screen text-white" style={{ background: '#0a0a0a' }}>
      <header className="flex items-center justify-between gap-4 px-5 sm:px-10 pt-6 pb-4">
        <Link
          to="/"
          className="text-lg font-bold uppercase"
          style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
        >
          Теория
        </Link>
        <nav className="flex items-center gap-5">
          <Link
            to="/events"
            className="text-[12px] tracking-[0.18em] uppercase font-semibold transition-opacity hover:opacity-70"
            style={{ fontFamily: 'Manrope, sans-serif', color: 'rgba(255,255,255,0.7)' }}
          >
            События
          </Link>
          <Link
            to="/app/home"
            className="text-[12px] tracking-[0.18em] uppercase font-semibold transition-opacity hover:opacity-70"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            В приложение
          </Link>
        </nav>
      </header>

      <main className="px-5 sm:px-10 pb-24 max-w-2xl mx-auto pt-14 sm:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1
            className="text-4xl sm:text-5xl font-bold uppercase mb-5"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
          >
            Бронирование
          </h1>
          <p
            className="text-base sm:text-lg leading-relaxed mb-10 max-w-lg"
            style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Manrope, sans-serif' }}
          >
            Оставьте заявку на дату и время. Стол подберёт администратор.
          </p>

          {success ? (
            <div
              className="rounded-3xl p-6 sm:p-8 mb-6"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Check size={22} />
                <p className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Заявка принята
                </p>
              </div>
              <p className="text-sm mb-2" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Manrope, sans-serif' }}>
                {formatDateLabel(success.date, dateOptions)} · {success.time} · {success.guestsCount}{' '}
                {success.guestsCount === 1 ? 'гость' : success.guestsCount < 5 ? 'гостя' : 'гостей'}
              </p>
              <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}>
                Администратор подтвердит бронь и посадит вас за свободный стол.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSuccess(null);
                  setComment('');
                }}
                className="inline-flex items-center justify-center px-7 py-4 rounded-2xl font-semibold text-sm tracking-[0.12em] uppercase"
                style={{ background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }}
              >
                Новая заявка
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5 mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label style={labelStyle}>Дата</label>
                  <select value={date} onChange={(e) => setDate(e.target.value)} style={fieldStyle} required>
                    {dateOptions.map((d) => (
                      <option key={d.date} value={d.date} style={{ background: '#121212', color: '#fff' }}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Время</label>
                  <select
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    style={fieldStyle}
                    required
                    disabled={loadingSlots || availableSlots.length === 0}
                  >
                    {loadingSlots ? (
                      <option value="">Загрузка…</option>
                    ) : availableSlots.length === 0 ? (
                      <option value="">Нет свободного времени</option>
                    ) : (
                      availableSlots.map((s) => (
                        <option key={s.time} value={s.time} style={{ background: '#121212', color: '#fff' }}>
                          {s.time}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Гости</label>
                  <select
                    value={guestsCount}
                    onChange={(e) => setGuestsCount(Number(e.target.value))}
                    style={fieldStyle}
                    required
                  >
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n} style={{ background: '#121212', color: '#fff' }}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label style={labelStyle}>Имя</label>
                  <input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    style={fieldStyle}
                    placeholder="Как к вам обращаться"
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Телефон</label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    style={fieldStyle}
                    placeholder="+7 900 000-00-00"
                    required
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Комментарий</label>
                <input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  style={fieldStyle}
                  placeholder="Пожелания к брони"
                />
              </div>

              {error && (
                <p className="text-sm" style={{ color: '#ff8f8f', fontFamily: 'Manrope, sans-serif' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || loadingSlots || !time}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-4 rounded-2xl font-semibold text-sm tracking-[0.12em] uppercase transition-transform active:scale-[0.98] disabled:opacity-50"
                style={{ background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }}
              >
                {submitting ? <Loader2 size={17} className="animate-spin" /> : null}
                Отправить заявку
              </button>
            </form>
          )}

          <div
            className="rounded-3xl p-6 sm:p-8 mb-6"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <p
              className="text-[11px] tracking-[0.2em] uppercase mb-3"
              style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
            >
              Или по телефону
            </p>
            <a
              href={`tel:${BOOKING_PHONE_TEL}`}
              className="block text-2xl sm:text-3xl font-bold mb-6 hover:opacity-80 transition-opacity"
              style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}
            >
              {BOOKING_PHONE_DISPLAY}
            </a>
            <a
              href={`tel:${BOOKING_PHONE_TEL}`}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-4 rounded-2xl font-semibold text-sm tracking-[0.12em] uppercase transition-transform active:scale-[0.98]"
              style={{
                background: 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              <Phone size={17} /> Позвонить
            </a>
          </div>

          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Manrope, sans-serif' }}>
            Адрес: г. Долгопрудный, ул. Первомайская, 9/4
          </p>
        </motion.div>
      </main>

      <footer className="px-5 sm:px-10 py-10 border-t border-white/10">
        <div className="max-w-2xl mx-auto flex flex-wrap gap-4 justify-between text-[12px] tracking-[0.12em] uppercase">
          <Link to="/" style={{ fontFamily: 'Manrope, sans-serif' }} className="hover:opacity-70">
            На главную
          </Link>
          <Link to="/menu" style={{ fontFamily: 'Manrope, sans-serif' }} className="hover:opacity-70">
            Меню
          </Link>
          <Link to="/events" style={{ fontFamily: 'Manrope, sans-serif' }} className="hover:opacity-70">
            События
          </Link>
        </div>
      </footer>
    </div>
  );
}
